import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Download,
  BarChart3,
  ShieldAlert,
  Globe,
  Monitor,
  CheckCircle,
  Calendar,
  Clock,
  Loader2,
  ChevronDown,
  History,
  FileBarChart,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  BarChart3: BarChart3,
  ShieldAlert: ShieldAlert,
  Globe: Globe,
  Monitor: Monitor,
  CheckCircle: CheckCircle,
  FileText: FileText,
};

export default function Reports() {
  const [reportTypes, setReportTypes] = useState([]);
  const [reportHistory, setReportHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [typesRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/reports/types`, { headers }),
        axios.get(`${API}/api/reports/history`, { headers })
      ]);
      setReportTypes(typesRes.data.report_types || []);
      setReportHistory(historyRes.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (reportId, periodDays = 30) => {
    setGenerating(reportId);
    
    const endpointMap = {
      'full_security': '/api/reports/generate/full-security',
      'executive_summary': '/api/reports/generate/executive-summary',
      'threat_analysis': '/api/reports/generate/threat-analysis',
      'dns_security': '/api/reports/generate/dns-security',
      'endpoint_protection': '/api/reports/generate/endpoint-protection',
      'compliance': '/api/reports/generate/compliance'
    };

    const endpoint = endpointMap[reportId];
    if (!endpoint) {
      toast.error('Unknown report type');
      setGenerating(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}${endpoint}`, null, {
        params: { period_days: periodDays },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${reportId}_${new Date().toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report generated successfully!');
      fetchData(); // Refresh history
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileBarChart className="w-8 h-8 text-blue-500" />
            Reports & Analytics
          </h1>
          <p className="text-slate-400 mt-1">
            Generate beautiful PDF reports for your security posture
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Report Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reportTypes.map((report, index) => {
          const IconComponent = iconMap[report.icon] || FileText;
          const isGenerating = generating === report.id;
          
          return (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="bg-slate-900 border-slate-800 hover:border-blue-500/50 transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-blue-500" />
                    </div>
                    <Badge variant="outline" className="text-xs">PDF</Badge>
                  </div>
                  <CardTitle className="text-lg text-white mt-4">{report.name}</CardTitle>
                  <CardDescription className="text-slate-400">
                    {report.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-4">
                    <Calendar className="w-3 h-3" />
                    <span>Available periods: {report.periods?.join(', ')} days</span>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        disabled={isGenerating}
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Generate Report
                            <ChevronDown className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                      {report.periods?.map(period => (
                        <DropdownMenuItem
                          key={period}
                          onClick={() => generateReport(report.id, period)}
                          className="text-slate-200 hover:bg-slate-700 cursor-pointer"
                        >
                          Last {period} days
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Report History */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-blue-500" />
            <CardTitle className="text-white">Recent Reports</CardTitle>
          </div>
          <CardDescription className="text-slate-400">
            Previously generated reports
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reportHistory.length > 0 ? (
            <div className="space-y-3">
              {reportHistory.map((report, index) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{report.name}</p>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(report.generated_at).toLocaleDateString()}
                        </span>
                        <span>•</span>
                        <span>{report.period}</span>
                        <span>•</span>
                        <span>{report.generated_by}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20"
                    onClick={() => generateReport(report.type, parseInt(report.period))}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Regenerate
                  </Button>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No reports generated yet</p>
              <p className="text-sm">Generate your first report above</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-600/20 to-blue-900/20 border-blue-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-300">Reports Generated</p>
                <p className="text-3xl font-bold text-white">{reportHistory.length}</p>
              </div>
              <FileBarChart className="w-10 h-10 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-600/20 to-green-900/20 border-green-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-300">Report Types</p>
                <p className="text-3xl font-bold text-white">{reportTypes.length}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-600/20 to-purple-900/20 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-300">This Month</p>
                <p className="text-3xl font-bold text-white">{reportHistory.length}</p>
              </div>
              <Calendar className="w-10 h-10 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-600/20 to-amber-900/20 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-300">Scheduled</p>
                <p className="text-3xl font-bold text-white">0</p>
              </div>
              <Clock className="w-10 h-10 text-amber-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
